import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Plus, CheckCircle2, X, Edit3, ExternalLink, RefreshCw, FileText, CreditCard, Send } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  INDIA COMPLIANCE — Phase 4 hub: ABDM/ABHA · Govt Schemes · DigiLocker · EMI
// ═══════════════════════════════════════════════════════════════════

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const IndiaCompliancePage = ({ API_BASE, currentUser }) => {
  const [tab, setTab] = useState('abdm');
  const [patients, setPatients] = useState([]);
  const [picker, setPicker] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [abha, setAbha] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [claims, setClaims] = useState([]);
  const [emiPlans, setEmiPlans] = useState([]);
  const [abdmStatus, setAbdmStatus] = useState({ sandbox: true });
  const [dlStatus, setDlStatus] = useState({ sandbox: true });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const showMsg = (t, x) => { setMsg({ type: t, text: x }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, sc, ben, cl, emi, ad, dl] = await Promise.all([
        axios.get(`${API_BASE}/patients`),
        axios.get(`${API_BASE}/gov/schemes`),
        axios.get(`${API_BASE}/gov/beneficiaries`),
        axios.get(`${API_BASE}/gov/claims`),
        axios.get(`${API_BASE}/emi-plans`),
        axios.get(`${API_BASE}/abdm/status`),
        axios.get(`${API_BASE}/digilocker/status`),
      ]);
      setPatients(p.data || []);
      setSchemes(sc.data || []);
      setBeneficiaries(ben.data || []);
      setClaims(cl.data || []);
      setEmiPlans(emi.data || []);
      setAbdmStatus(ad.data); setDlStatus(dl.data);
    } catch {}
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const q = picker.trim().toLowerCase();
    if (!q) return setFiltered([]);
    setFiltered(patients.filter(p => (p.name || '').toLowerCase().includes(q) || (p.phone || '').includes(q) || (p.uhid || '').toLowerCase().includes(q)).slice(0, 6));
  }, [picker, patients]);

  // ── ABDM/ABHA actions ──
  const fetchAbha = async (pid) => {
    try { const r = await axios.get(`${API_BASE}/abdm/abha/${pid}`); setAbha(r.data); }
    catch { setAbha(null); }
  };
  const createAbha = async () => {
    if (!selectedPatient) return;
    try {
      const r = await axios.post(`${API_BASE}/abdm/abha/create`, { patientId: selectedPatient._id, by: currentUser?.fullName });
      setAbha(r.data.profile);
      showMsg('success', `✓ ABHA generated: ${r.data.profile.abhaNumber} ${r.data.sandbox ? '(sandbox)' : ''}`);
    } catch (e) { showMsg('error', e.response?.data?.error || e.message); }
  };
  const linkAbha = async (abhaNumber) => {
    if (!selectedPatient || !abhaNumber) return;
    try {
      const r = await axios.post(`${API_BASE}/abdm/abha/link`, { patientId: selectedPatient._id, abhaNumber });
      setAbha(r.data.profile);
      showMsg('success', '✓ ABHA linked');
    } catch (e) { showMsg('error', e.message); }
  };

  // ── DigiLocker action ──
  const fetchDigiLocker = async (dataType) => {
    if (!selectedPatient) return;
    try {
      if (dlStatus.sandbox) {
        const r = await axios.post(`${API_BASE}/digilocker/mock-fetch`, { patientId: selectedPatient._id, dataType });
        showMsg('success', `✓ Sandbox ${dataType} fetched: ${JSON.stringify(r.data.fetched).slice(0, 100)}`);
      } else {
        const r = await axios.get(`${API_BASE}/digilocker/auth-url`, { params: { patientId: selectedPatient._id, dataType } });
        window.open(r.data.url, '_blank');
      }
    } catch (e) { showMsg('error', e.message); }
  };

  // ── Seed schemes ──
  const seedSchemes = async () => {
    try { const r = await axios.post(`${API_BASE}/gov/schemes/seed`); showMsg('success', `Seeded ${r.data.created} schemes`); loadAll(); }
    catch (e) { showMsg('error', e.message); }
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, #FF9933, #138808)`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>🇮🇳 India Compliance & Govt Integrations</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.95, fontSize: 13 }}>
              ABDM/ABHA · PMJAY · RGHS · CGHS · DigiLocker · UPI Autopay
            </p>
          </div>
          <button onClick={loadAll} disabled={loading} style={{ ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
            <RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh
          </button>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`, fontWeight: 600, fontSize: 13.5 }}>{msg.text}</div>
      )}

      {/* Patient selector */}
      <div style={{ ...cardStyle, padding: 12, marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>Select Patient (for ABHA/DigiLocker actions)</label>
        <input value={picker} onChange={e => setPicker(e.target.value)} placeholder="Search by name / phone / UHID" style={{ ...inputStyle, marginTop: 4 }} />
        {filtered.length > 0 && !selectedPatient && (
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 6, marginTop: 4 }}>
            {filtered.map(p => (
              <div key={p._id} onClick={() => { setSelectedPatient(p); setPicker(''); setFiltered([]); fetchAbha(p._id); }}
                style={{ padding: 8, cursor: 'pointer', borderBottom: `1px solid ${theme.borderLight}` }}>
                <strong>{p.name}</strong> · {p.phone} · {p.uhid}
              </div>
            ))}
          </div>
        )}
        {selectedPatient && (
          <div style={{ marginTop: 6, padding: 8, background: theme.primary + '15', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><strong>{selectedPatient.name}</strong> · {selectedPatient.phone} · {selectedPatient.uhid}</span>
            <button onClick={() => { setSelectedPatient(null); setAbha(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {[
          { k: 'abdm',   l: '💚 ABHA / ABDM',    c: abha ? '✓' : '' },
          { k: 'gov',    l: '📋 Govt Schemes',   c: schemes.length },
          { k: 'claims', l: '📑 Claims',         c: claims.length },
          { k: 'digilocker', l: '🔒 DigiLocker', c: dlStatus.sandbox ? 'SBX' : 'LIVE' },
          { k: 'emi',    l: '💳 EMI / Autopay',  c: emiPlans.length },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: '8px 14px', borderRadius: 8,
            border: `1.5px solid ${tab === t.k ? '#FF9933' : theme.border}`,
            background: tab === t.k ? '#FF993315' : 'white',
            color: tab === t.k ? '#cc6600' : theme.textMuted,
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>{t.l} {t.c !== '' && <span style={{ marginLeft: 4, background: 'rgba(0,0,0,0.08)', padding: '1px 6px', borderRadius: 999, fontSize: 11 }}>{t.c}</span>}</button>
        ))}
      </div>

      {/* ABDM tab */}
      {tab === 'abdm' && (
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0 }}>💚 Ayushman Bharat Health Account (ABHA)</h3>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: abdmStatus.sandbox ? '#fef3c7' : '#d1fae5', color: abdmStatus.sandbox ? '#92400e' : '#065f46' }}>
              {abdmStatus.sandbox ? 'SANDBOX' : 'LIVE'}
            </span>
          </div>
          {!selectedPatient ? (
            <p style={{ color: theme.textMuted }}>Select a patient above to manage ABHA.</p>
          ) : abha ? (
            <div>
              <div style={{ padding: 14, background: '#ecfdf5', borderRadius: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: '#065f46', fontWeight: 700, marginBottom: 4 }}>✓ ABHA Linked</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#065f46', fontFamily: 'monospace' }}>{abha.abhaNumber}</div>
                <div style={{ fontSize: 13, color: '#047857' }}>{abha.abhaAddress}</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
                  KYC: <strong>{abha.kycStatus}</strong> · Linked: {fmt(abha.linkedAt)} · Records pushed: {abha.recordsPushed?.length || 0}
                </div>
              </div>
              <button onClick={() => { setAbha(null); fetchAbha(selectedPatient._id); }} style={{ ...btnSm }}>
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 13, color: theme.textMuted }}>No ABHA linked yet for this patient.</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={createAbha} style={btnStyle}><Plus size={14} style={{ marginRight: 6 }} /> Generate New ABHA</button>
                <LinkExisting onLink={linkAbha} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Govt Schemes tab */}
      {tab === 'gov' && (
        <div>
          {schemes.length === 0 ? (
            <div style={{ ...cardStyle, padding: 20, textAlign: 'center' }}>
              <p style={{ color: theme.textMuted }}>No schemes registered yet.</p>
              <button onClick={seedSchemes} style={btnStyle}>Seed Default Schemes (PMJAY, RGHS, CGHS, ECHS, ESI)</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {schemes.map(s => (
                <div key={s._id} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${s.empanelled ? '#16a34a' : '#94a3b8'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#cc6600', background: '#FF993318', padding: '2px 7px', borderRadius: 4 }}>{s.type}</span>
                        <strong style={{ fontSize: 14, color: theme.navy }}>{s.name}</strong>
                        {s.empanelled && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>✓ EMPANELLED</span>}
                      </div>
                      <div style={{ fontSize: 12, color: theme.textMuted }}>
                        {s.authority} {s.state && '· ' + s.state} {s.contactPhone && '· ☏ ' + s.contactPhone}
                      </div>
                    </div>
                    {s.portalUrl && <a href={s.portalUrl} target="_blank" rel="noreferrer" style={{ ...btnSm, textDecoration: 'none' }}><ExternalLink size={11} /> Portal</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Claims tab */}
      {tab === 'claims' && (
        <div>
          {claims.length === 0 ? (
            <div style={{ ...cardStyle, padding: 30, textAlign: 'center', color: theme.textMuted }}>No claims filed yet.</div>
          ) : claims.map(c => (
            <div key={c._id} style={{ ...cardStyle, padding: 12, marginBottom: 6, borderLeft: `4px solid ${{draft:'#94a3b8',submitted:'#2563eb',approved:'#16a34a',rejected:'#dc2626',paid:'#16a34a'}[c.status] || '#94a3b8'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#FF993318', color: '#cc6600' }}>{c.schemeType}</span>
                  <strong style={{ marginLeft: 6 }}>{c.patientName}</strong>
                  <span style={{ fontSize: 10, color: theme.textMuted, marginLeft: 4, textTransform: 'uppercase', fontWeight: 700 }}>· {c.status}</span>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>
                    Claimed ₹{c.totalClaimed?.toLocaleString('en-IN') || 0}
                    {c.totalApproved > 0 && ` · Approved ₹${c.totalApproved.toLocaleString('en-IN')}`}
                    {c.totalPaid > 0 && ` · Paid ₹${c.totalPaid.toLocaleString('en-IN')}`}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  {c.claimReferenceNumber || '—'}<br/>{fmt(c.createdAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DigiLocker tab */}
      {tab === 'digilocker' && (
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0 }}>🔒 DigiLocker — Aadhaar/PAN Fetch</h3>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: dlStatus.sandbox ? '#fef3c7' : '#d1fae5', color: dlStatus.sandbox ? '#92400e' : '#065f46' }}>
              {dlStatus.sandbox ? 'SANDBOX' : 'LIVE'}
            </span>
          </div>
          {!selectedPatient ? (
            <p style={{ color: theme.textMuted }}>Select a patient to fetch documents.</p>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => fetchDigiLocker('aadhaar')} style={btnStyle}>Fetch Aadhaar</button>
              <button onClick={() => fetchDigiLocker('pan')} style={{ ...btnStyle, background: '#7c3aed' }}>Fetch PAN</button>
              <button onClick={() => fetchDigiLocker('driving')} style={{ ...btnStyle, background: '#0891b2' }}>Fetch DL</button>
            </div>
          )}
          {dlStatus.sandbox && (
            <p style={{ marginTop: 12, padding: 10, background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#78350f' }}>
              ⚠ Sandbox mode — set <code>DIGILOCKER_CLIENT_ID</code> in .env to enable real OAuth.
            </p>
          )}
        </div>
      )}

      {/* EMI tab */}
      {tab === 'emi' && (
        <div>
          {emiPlans.length === 0 ? (
            <div style={{ ...cardStyle, padding: 30, textAlign: 'center', color: theme.textMuted }}>No EMI plans created yet.</div>
          ) : emiPlans.map(p => {
            const paid = p.installments.filter(i => i.status === 'paid').length;
            const pct = Math.round((paid / p.numInstallments) * 100);
            return (
              <div key={p._id} style={{ ...cardStyle, padding: 12, marginBottom: 6, borderLeft: '4px solid #FF9933' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: 14, color: theme.navy }}>{p.patientName}</strong>
                    <div style={{ fontSize: 12, color: theme.textMuted }}>
                      ₹{p.totalAmount.toLocaleString('en-IN')} · {paid}/{p.numInstallments} EMIs paid · {p.status}
                      {p.autopayEnabled && <span style={{ marginLeft: 6, color: '#16a34a' }}>· Autopay {p.mandateStatus}</span>}
                    </div>
                  </div>
                  <div style={{ width: 80, height: 6, background: theme.borderLight, borderRadius: 3 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#FF9933', borderRadius: 3 }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LinkExisting = ({ onLink }) => {
  const [n, setN] = useState('');
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input value={n} onChange={e => setN(e.target.value)} placeholder="Existing ABHA Number" style={{ ...inputStyle, padding: '8px 10px', minWidth: 220 }} />
      <button onClick={() => onLink(n)} disabled={!n} style={{ ...btnStyle, marginTop: 0 }}>Link</button>
    </div>
  );
};

const btnSm = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 11px', borderRadius: 6, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 12, fontWeight: 600 };

export default IndiaCompliancePage;
